import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import PlayerDetailModal from "@/components/players/PlayerDetailModal";
import { MessageSquare, CheckCircle, Clock, User, MapPin } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Player } from "@/lib/types";
import { useToast } from "@/hooks/use-toast";

interface PlayerUpdate {
  id: number;
  name: string;
  email: string;
  position?: string[] | null;
  current_club: string;
  last_email_date: string;
  email_update_count: number;
  preview: string;
}

export default function PlayerUpdates() {
  const { toast } = useToast();
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch players with updates
  const { data: updates, isLoading } = useQuery({
    queryKey: ["player-updates"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/players/updates/list");
      if (!response.ok) {
        throw new Error("Failed to fetch updates");
      }
      const data = await response.json();
      return data as PlayerUpdate[];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch full player data when modal opens
  const { data: fullPlayer } = useQuery({
    queryKey: ["player", selectedPlayer?.id],
    queryFn: async () => {
      if (!selectedPlayer) return null;
      const response = await apiRequest("GET", `/api/players/${selectedPlayer.id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch player");
      }
      const data = await response.json();
      return data as Player;
    },
    enabled: !!selectedPlayer,
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (playerId: number) => {
      await apiRequest("POST", `/api/players/${playerId}/mark-read`);
    },
    onSuccess: (_, playerId) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["player-updates"] });
      queryClient.invalidateQueries({ queryKey: ["player-updates-count"] });
      queryClient.invalidateQueries({ queryKey: ["player", playerId] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/players/updates/mark-all-read");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-updates"] });
      queryClient.invalidateQueries({ queryKey: ["player-updates-count"] });
      toast({
        title: "All updates marked as read",
        description: "Player updates have been cleared",
      });
    },
  });

  const handlePlayerClick = (update: PlayerUpdate) => {
    // Set selected player with basic info
    setSelectedPlayer({
      id: update.id,
      name: update.name,
      email: update.email,
      position: update.position,
      current_club: update.current_club,
    } as Player);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    // Mark as read when closing modal
    if (selectedPlayer) {
      markAsReadMutation.mutate(selectedPlayer.id);
    }
    setIsModalOpen(false);
    setSelectedPlayer(null);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardHeader>
            <CardTitle>Player Updates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Player Updates
            {updates && updates.length > 0 && (
              <Badge variant="default">{updates.length} new</Badge>
            )}
          </CardTitle>
          {updates && updates.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isPending}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark All as Read
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {!updates || !Array.isArray(updates) || updates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No new player messages</p>
              <p className="text-sm mt-2">
                When players email you again, they'll appear here
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-4">
                {updates.map((update) => (
                  <Card
                    key={update.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handlePlayerClick(update)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">
                              {update.name}
                            </h3>
                            <Badge variant="secondary" className="text-xs">
                              {update.email_update_count} messages
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            {update.position && update.position.length > 0 && (
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {update.position.join(", ")}
                              </div>
                            )}
                            {update.current_club && (
                              <div className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {update.current_club}
                              </div>
                            )}
                          </div>

                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                            {update.preview}
                          </p>

                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(new Date(update.last_email_date), {
                              addSuffix: true,
                            })}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Player Detail Modal */}
      {selectedPlayer && fullPlayer && (
        <PlayerDetailModal
          player={fullPlayer}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          shortlists={[]} // You may want to fetch shortlists here
        />
      )}
    </div>
  );
}